// Consult-note API fns — carved verbatim from `src/api/scribeAPIV2.ts`,
// `src/api/scribeAPI.ts` (v1 email share), and `src/api/scribeAPIV2_1.ts`
// (the stream aborts — the registry's first v2.1 consumers) with the D4
// family; all three hubs now re-export from here. Transport comes from the
// `@heidi/api` configured-client registry; request paths, params, and bodies
// are byte-identical. Response/request types are the same generated-schema
// aliases the hubs used (`src/types` keeps its rows for its other importers).
//
// ARCFE-1096 adds the five remaining consult-note hub endpoints — the whole
// residue the ARCFE-1060 arm widening exposed, plus the three that predated
// it — so this family's `endpoints` claim is empty and the `api` dimension
// can read Green on a measurement rather than on a judgement. The v2.2 pair
// makes this the registry's first `scribeHttpV2_2` consumer, exactly as the
// stream aborts were its first v2.1 consumers.

import { getScribeHttpClients } from '@heidi/api/client';
import { type components } from '@heidi/schema/ml-scribe';

import { type ConsultNoteWithSectionMappings } from '../../contracts';

type GetConsultNotesResponse = components['schemas']['GetConsultNotesResponse'];
type GetConsultNoteTimelineResponse =
  components['schemas']['GetConsultNoteTimelineResponse'];
type UpdateConsultNoteRequest = components['schemas']['UpdateConsultNoteRequest'];
type UpdateConsultNoteResponse = components['schemas']['UpdateConsultNoteResponse'];
type FeedbackRequest = components['schemas']['FeedbackFormRequest'];
type AbortStreamRequest = components['schemas']['StopDocumentGenerationRequest'];
type ShareConsultNoteRequest = components['schemas']['ShareConsultNoteRequest'];
type ShareConsultNotesResponse = components['schemas']['ShareConsultNotesResponse'];
type ConvertPlainTextNoteToSectionalNoteResponse =
  components['schemas']['ConvertPlainTextNoteToSectionalNoteResponse'];

/**
 * The INT-4603 section-link request/response pair, moved beside its fn from
 * `src/types/index.ts` (which keeps its rows for other importers). The
 * response is NOT the bare generated shape: `consult_note` is widened with
 * `section_mappings`, which newer backends return before the OpenAPI schema
 * exposes it. Reproduced verbatim — narrowing it here would silently drop the
 * field the tiptap section-link mutation reads.
 */
export type UpdateJSONConsultNoteSectionLinkRequest =
  components['schemas']['UpdateConsultNoteSectionLinkRequest'];
export type UpdateJSONConsultNoteSectionLinkResponse = Omit<
  components['schemas']['UpdateConsultNoteSectionLinkResponse'],
  'consult_note'
> & {
  consult_note: ConsultNoteWithSectionMappings<
    components['schemas']['ConsultNoteResponseV2']
  >;
};

type SideBySideConsultNoteSetupRequest =
  components['schemas']['SideBySideConsultNoteSetupRequest'];
type SideBySideConsultNoteSetupResponse =
  components['schemas']['SideBySideConsultNoteSetupResponse'];
type SideBySideConsultNoteConfirmRequest =
  components['schemas']['SideBySideConsultNoteConfirmRequest'];
type SideBySideConsultNoteConfirmResponse =
  components['schemas']['SideBySideConsultNoteConfirmResponse'];

export const getConsultNotes = (sessionId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<GetConsultNotesResponse>(
    `/sessions/${sessionId}/consult-note`
  );
};

export const getSessionConsultNoteTimeline = (sessionId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<GetConsultNoteTimelineResponse>(
    `/sessions/${sessionId}/consult-note-timeline`
  );
};

export const revertSessionConsultNote = (
  sessionId: string,
  consultNoteId: string,
  targetNoteId: string | null,
  targetEditId: string | null
) => {
  return getScribeHttpClients().scribeHttpV2.patch<GetConsultNoteTimelineResponse>(
    `/sessions/${sessionId}/consult-note/${consultNoteId}/revert`,
    { target_note_id: targetNoteId, target_edit_id: targetEditId }
  );
};

export const updateConsultNote = (input: {
  request: UpdateConsultNoteRequest;
  sessionId: string;
}) => {
  return getScribeHttpClients().scribeHttpV2.patch<UpdateConsultNoteResponse>(
    `/sessions/${input.sessionId}/consult-note`,
    input.request
  );
};

export const submitConsultNoteFeedback = ({
  session_id,
  consult_note_id,
  ...input
}: {
  session_id: string;
  consult_note_id: string;
} & FeedbackRequest) => {
  return getScribeHttpClients().scribeHttpV2.post<unknown>(
    `/sessions/${session_id}/consult-note/${consult_note_id}/feedback`,
    { feedback: input }
  );
};

export const submitDocumentFeedback = ({
  session_id,
  document_id,
  ...input
}: {
  session_id: string;
  document_id: string;
} & FeedbackRequest) => {
  return getScribeHttpClients().scribeHttpV2.post<unknown>(
    `/sessions/${session_id}/documents/${document_id}/feedback`,
    { feedback: input }
  );
};

/**
 * Send a consult note to one or more email addresses.
 *
 * The response payload includes `sent_recipients` and `failed_recipients`
 * arrays so the FE can surface partial-failure UX (e.g. "sent to 2 of 3").
 */
export type ShareConsultNoteRecipientsRequest = {
  recipients: string[];
  content?: string | null;
};

export const shareConsultNoteByEmail = (
  sessionId: string,
  payload: ShareConsultNoteRecipientsRequest
) => {
  const body: ShareConsultNoteRequest = {
    recipients: payload.recipients,
    ...(payload.content !== undefined ? { content: payload.content } : {}),
  };
  return getScribeHttpClients().scribeHttp.post<ShareConsultNotesResponse>(
    `/sessions/${sessionId}/share-consult-note`,
    body
  );
};

export const postAbortConsultNoteStream = ({
  sessionId,
  content,
}: {
  sessionId: string;
  content: string;
}) => {
  const requestBody: AbortStreamRequest = {
    content,
  };
  return getScribeHttpClients().scribeHttpV2_1.post(
    `/sessions/${sessionId}/consult-note/stop`,
    requestBody
  );
};

export const postAbortDocumentStream = ({
  sessionId,
  documentId,
  content,
}: {
  sessionId: string;
  documentId: string;
  content: string;
}) => {
  const requestBody: AbortStreamRequest = {
    content,
  };
  return getScribeHttpClients().scribeHttpV2_1.post(
    `/sessions/${sessionId}/documents/${documentId}/stop`,
    requestBody
  );
};

// ---------------------------------------------------------------------------
// The ARCFE-1096 residue: five fns moved verbatim from three hubs.
// ---------------------------------------------------------------------------

/**
 * `POST /sessions/{id}/consult-note/generate-insights` — moved from
 * `src/api/scribeAPIV2_1.ts`. Returns the raw boolean the hub returned.
 */
export const noteGenerateInsights = ({ sessionId }: { sessionId: string }) => {
  return getScribeHttpClients().scribeHttpV2_1.post<boolean>(
    `/sessions/${sessionId}/consult-note/generate-insights`
  );
};

/**
 * `POST /sessions/{id}/consult-note/sectional-note-conversion` — moved from
 * `src/api/scribeAPIV2_1.ts`. Positional `sessionId`, kept as-is: the legacy
 * stream-response module calls it positionally.
 */
export const postConvertNoteTextToSectional = (sessionId: string) => {
  return getScribeHttpClients().scribeHttpV2_1.post<ConvertPlainTextNoteToSectionalNoteResponse>(
    `/sessions/${sessionId}/consult-note/sectional-note-conversion`
  );
};

/**
 * `PATCH /sessions/{id}/consult-note:link-section` — moved from
 * `src/api/scribeAPIV2.ts`. The `sessionId` is destructured out of the
 * request; the body carries only the two link fields, verbatim.
 */
export const updateJSONConsultNoteSectionLink = ({
  section_heading,
  linked_ehr_section_id,
  sessionId,
}: UpdateJSONConsultNoteSectionLinkRequest & {
  sessionId: string;
}) => {
  return getScribeHttpClients().scribeHttpV2.patch<UpdateJSONConsultNoteSectionLinkResponse>(
    `/sessions/${sessionId}/consult-note:link-section`,
    {
      section_heading,
      linked_ehr_section_id,
    }
  );
};

/**
 * `POST /sessions/{id}/consult-note/setup` — moved from
 * `src/api/scribeAPIV2_2.ts`. The `signal` passthrough is load-bearing: the
 * side-by-side note-gen flow aborts the setup call when the user switches
 * template mid-generation.
 */
export const postSetupConsultNote = async (
  sessionId: string,
  reqBody: SideBySideConsultNoteSetupRequest,
  signal?: AbortSignal
) => {
  const resp =
    await getScribeHttpClients().scribeHttpV2_2.post<SideBySideConsultNoteSetupResponse>(
      `/sessions/${sessionId}/consult-note/setup`,
      reqBody,
      { signal }
    );
  return resp;
};

/**
 * `POST /sessions/{id}/consult-note/confirm` — moved from
 * `src/api/scribeAPIV2_2.ts`. Same `signal` passthrough as its setup sibling.
 */
export const postConfirmConsultNote = async (
  sessionId: string,
  reqBody: SideBySideConsultNoteConfirmRequest,
  signal?: AbortSignal
) => {
  const resp =
    await getScribeHttpClients().scribeHttpV2_2.post<SideBySideConsultNoteConfirmResponse>(
      `/sessions/${sessionId}/consult-note/confirm`,
      reqBody,
      { signal }
    );
  return resp;
};
